import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { api } from '../../lib/api';

const fmt = (n) => Number(n).toLocaleString();

// Checks an odometer/hours reading against the vehicle's history before saving.
export const useReadingTypoGuard = () => {
  const [warning, setWarning] = useState(null);

  const guard = async ({ vehicleId, date, reading, excludeId }, proceed) => {
    const value = parseFloat(reading);
    if (!vehicleId || !date || !value) return proceed();
    try {
      const r = await api.checkReading(vehicleId, { date, reading: value, exclude_id: excludeId || null });
      if (r?.suspect) {
        setWarning({ ...r, proceed });
        return;
      }
    } catch {
      // never block saving because the check itself failed
    }
    return proceed();
  };

  const dialog = (
    <AlertDialog open={!!warning} onOpenChange={(o) => { if (!o) setWarning(null); }}>
      <AlertDialogContent data-testid="reading-typo-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Is {warning ? `${fmt(warning.reading)} ${warning.unit}` : 'this reading'} correct?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              <p data-testid="reading-typo-reason">This looks like a possible typo. {warning?.reason}</p>
              {warning?.previous && (
                <p className="font-data text-xs">
                  Previous: {fmt(warning.previous.reading)} {warning.unit} on {warning.previous.date}
                  {warning.next && ` · Later: ${fmt(warning.next.reading)} ${warning.unit} on ${warning.next.date}`}
                </p>
              )}
              {warning?.expected_reading && (
                <p className="font-data text-xs" data-testid="reading-typo-expected">
                  Expected around {fmt(warning.expected_reading)} {warning.unit}
                </p>
              )}
              <p className="text-xs">If you save it anyway, it is kept on the entry but ignored for service dates until later readings confirm it.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reading-typo-fix-btn">Fix reading</AlertDialogCancel>
          <AlertDialogAction
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => { const p = warning?.proceed; setWarning(null); if (p) p(); }}
            data-testid="reading-typo-save-anyway-btn"
          >
            Save anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { guard, dialog };
};
